import React, { useCallback, useEffect, useRef, useState } from 'react';

const AUTOPLAY_INTERVAL = 6000;
const ANIMATION_DURATION = 450;

export interface Step {
  id: number;
  title: string;
  description: string;
  screenshot: string;
  features?: ReadonlyArray<string>;
  benefits?: ReadonlyArray<string>;
}

interface StepCarouselProps {
  steps: ReadonlyArray<Step>;
  badgeText: string;
  badgeColor?: string;
  featuresLabel?: string;
  alignStart?: boolean;
}

const StepCarousel: React.FC<StepCarouselProps> = ({
  steps,
  badgeText,
  badgeColor = '#0052FF',
  featuresLabel = 'Features',
  alignStart = false
}) => {
  const totalSteps = steps.length;
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoplayRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeStep = steps[activeIndex];

  const startAnimation = useCallback(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    setIsAnimating(true);
    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, ANIMATION_DURATION);
  }, []);

  const restartAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
    autoplayRef.current = setInterval(() => {
      setActiveIndex((prev) => {
        const nextIndex = prev === totalSteps - 1 ? 0 : prev + 1;
        return nextIndex;
      });
      startAnimation();
    }, AUTOPLAY_INTERVAL);
  }, [startAnimation, totalSteps]);

  useEffect(() => {
    startAnimation();
    restartAutoplay();

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, [restartAutoplay, startAnimation]);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev === 0 ? totalSteps - 1 : prev - 1));
    startAnimation();
    restartAutoplay();
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev === totalSteps - 1 ? 0 : prev + 1));
    startAnimation();
    restartAutoplay();
  };

  const handleIndicatorClick = (index: number) => {
    setActiveIndex(index);
    startAnimation();
    restartAutoplay();
  };

  const listItems = activeStep.features || activeStep.benefits || [];

  return (
    <>
      {/* Mobile Mockup - Outside carousel on mobile */}
      <div className="step-mockup-mobile" style={{
        display: 'none',
        justifyContent: 'center',
        marginBottom: '20px',
        ...(isAnimating ? { animation: 'stepFadeSlide 0.45s ease both' } : {})
      }}>
        <div className="cb-phone-mockup" style={{
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div className="cb-phone-screen" style={{
            position: 'relative',
            overflow: 'hidden'
          }}>
            <img
              src={activeStep.screenshot}
              alt={`${activeStep.title} screenshot`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'top',
                display: 'block'
              }}
            />
          </div>
        </div>
      </div>

      <div
        className="step-carousel"
        style={{
          position: 'relative',
          ...(alignStart ? { width: '100%' } : {})
        }}
      >
        <div
          className="step-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 1fr) minmax(320px, 1fr)',
            gap: '60px',
            alignItems: 'center',
            ...(alignStart
              ? {
                  gridTemplateColumns: 'minmax(200px, 260px) minmax(320px, 380px)',
                  gap: '32px',
                  width: '100%',
                  maxWidth: '660px',
                  justifyItems: 'start',
                  justifyContent: 'start',
                  marginRight: 'auto'
                }
              : {})
          }}
        >
          {/* Desktop Mockup */}
          <div
            className="step-mockup-desktop"
            style={{
              display: 'flex',
              justifyContent: 'center',
              ...(alignStart ? { justifyContent: 'flex-start', width: '100%' } : {}),
              ...(isAnimating ? { animation: 'stepFadeSlide 0.45s ease both' } : {})
            }}
          >
            <div className="cb-phone-mockup">
              <div className="cb-phone-screen">
                <img
                  src={activeStep.screenshot}
                  alt={`${activeStep.title} screenshot`}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    objectPosition: 'top'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div
            className="step-content"
            style={{
              direction: 'ltr',
              ...(alignStart ? { width: '100%', maxWidth: '480px', textAlign: 'left' } : {}),
              ...(isAnimating ? { animation: 'stepFadeSlide 0.45s ease both' } : {})
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '12px',
                padding: '8px 16px',
                background: `rgba(0, 82, 255, 0.1)`,
                borderRadius: '24px',
                marginBottom: '24px'
              }}
            >
              <span
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: badgeColor,
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}
              >
                {badgeText} {activeIndex + 1} of {totalSteps}
              </span>
            </div>

          <h3 className="step-title" style={{
            fontSize: 'clamp(1.5rem, 4vw, 2rem)',
            fontWeight: 700,
            color: '#0A0B0D',
            marginBottom: '16px'
          }}>
            {activeStep.title}
          </h3>

          <p className="step-description" style={{
            fontSize: 'clamp(0.9rem, 2vw, 1.125rem)',
            color: '#5B616E',
            lineHeight: 1.6,
            marginBottom: '32px'
          }}>
            {activeStep.description}
          </p>

          {listItems.length > 0 && (
            <div style={{
              marginBottom: '32px'
            }}>
              <h4 style={{
                fontSize: '1rem',
                fontWeight: 600,
                color: '#0A0B0D',
                marginBottom: '16px'
              }}>
                {featuresLabel}:
              </h4>
              <ul className="cb-feature-list">
                {listItems.map((item, itemIndex) => (
                  <li key={itemIndex} className="cb-feature-item">
                    <div className="cb-checkmark">
                      <span className="cb-checkmark-icon">
                        ✓
                      </span>
                    </div>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div
        className="step-controls"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          marginTop: '48px',
          ...(alignStart
            ? { width: '100%', maxWidth: '660px', justifyContent: 'flex-start', marginLeft: 0, marginRight: 'auto' }
            : {})
        }}
      >
        <button
          type="button"
          onClick={handlePrev}
          aria-label="View previous step"
          className="step-nav step-nav-prev"
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '999px',
            border: '1px solid rgba(10, 11, 13, 0.1)',
            background: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            color: '#0A0B0D',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#F1F4FF';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
          }}
        >
          ←
        </button>

        <div
          className="step-indicators"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {steps.map((step, index) => (
            <button
              key={step.id}
              type="button"
              onClick={() => handleIndicatorClick(index)}
              aria-label={`View step ${index + 1}`}
              className="step-indicator"
              style={{
                width: index === activeIndex ? '32px' : '12px',
                height: '12px',
                borderRadius: '999px',
                border: 'none',
                background: index === activeIndex ? badgeColor : `rgba(0, 82, 255, 0.25)`,
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={handleNext}
          aria-label="View next step"
          className="step-nav step-nav-next"
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '999px',
            border: '1px solid rgba(10, 11, 13, 0.1)',
            background: 'white',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.25rem',
            color: '#0A0B0D',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#F1F4FF';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'white';
          }}
        >
          →
        </button>
      </div>

      <style>{`
        @keyframes stepFadeSlide {
          from {
            opacity: 0;
            transform: translateX(-24px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @media (max-width: 768px) {
          .step-mockup-mobile {
            display: flex !important;
          }

          .step-mockup-mobile .cb-phone-mockup {
            width: 200px !important;
            height: 400px !important;
            background: #000 !important;
            border-radius: 32px !important;
            padding: 4px !important;
            box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15) !important;
          }

          .step-mockup-mobile .cb-phone-screen {
            width: 100% !important;
            height: 100% !important;
            border-radius: 28px !important;
            overflow: hidden !important;
            background: #ffffff !important;
          }

          .step-mockup-desktop {
            display: none !important;
          }

          .step-carousel {
            background: linear-gradient(135deg, #F8F9FF 0%, #E7F3FF 100%) !important;
            border-radius: 24px !important;
            padding: 32px 20px !important;
            margin: 0 20px !important;
            position: relative !important;
          }

          .step-grid {
            grid-template-columns: 1fr !important;
            direction: ltr !important;
            gap: 0 !important;
            margin-bottom: 0 !important;
          }

          .step-content {
            text-align: left !important;
            order: 1 !important;
            padding: 0 !important;
          }

          .step-content > div:first-child {
            justify-content: flex-start !important;
          }

          .step-title {
            font-size: 1.5rem !important;
            margin-bottom: 12px !important;
            text-align: left !important;
          }

          .step-description {
            font-size: 1rem !important;
            text-align: left !important;
            margin-bottom: 20px !important;
            line-height: 1.6 !important;
          }

          .step-content .cb-feature-list {
            text-align: left !important;
            max-width: 100% !important;
            margin: 0 !important;
          }

          .step-content .cb-feature-item {
            justify-content: flex-start !important;
            font-size: 0.95rem !important;
            margin-bottom: 10px !important;
          }

          .step-controls {
            flex-direction: row !important;
            gap: 16px !important;
            margin-top: 28px !important;
            justify-content: space-between !important;
            padding: 0 !important;
          }

          .step-nav {
            width: 56px !important;
            height: 56px !important;
            font-size: 1.5rem !important;
            flex-shrink: 0 !important;
            background: white !important;
          }

          .step-indicators {
            gap: 8px !important;
            flex: 1 !important;
            justify-content: center !important;
          }

          .step-indicator {
            width: 20px !important;
            height: 10px !important;
          }
        }

        @media (max-width: 480px) {
          .step-mockup-mobile .cb-phone-mockup {
            width: 180px !important;
            height: 360px !important;
          }

          .step-carousel {
            padding: 28px 16px !important;
            margin: 0 16px !important;
          }

          .step-title {
            font-size: 1.35rem !important;
            margin-bottom: 10px !important;
          }

          .step-description {
            font-size: 0.95rem !important;
            margin-bottom: 18px !important;
            line-height: 1.55 !important;
          }

          .step-content .cb-feature-item {
            font-size: 0.9rem !important;
            margin-bottom: 8px !important;
          }

          .step-controls {
            margin-top: 24px !important;
          }

          .step-nav {
            width: 52px !important;
            height: 52px !important;
            font-size: 1.4rem !important;
          }

          .step-indicators {
            gap: 6px !important;
          }

          .step-indicator {
            width: 18px !important;
            height: 10px !important;
          }
        }
      `}</style>
      </div>
    </>
  );
};

export default StepCarousel;
